import { useEffect, useRef } from 'react';
import { User, UserDevice } from '../types';
import { useFirebase } from './useFirebase';

export const useDeviceTracking = (user: User | null, setUser: React.Dispatch<React.SetStateAction<User | null>>) => {
    const { updateUser } = useFirebase();
    const lastSyncRef = useRef<number>(0);

    useEffect(() => {
        if (!user) return;

        // Get or create local device ID
        let deviceId = localStorage.getItem('luca_device_id');
        if (!deviceId) {
            deviceId = `dev_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
            localStorage.setItem('luca_device_id', deviceId);
        }

        const userAgent = navigator.userAgent;
        const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(userAgent);

        // Determine OS
        let os = 'Unknown OS';
        if (userAgent.indexOf("Win") !== -1) os = "Windows";
        if (userAgent.indexOf("Mac") !== -1) os = "MacOS";
        if (userAgent.indexOf("Linux") !== -1) os = "Linux";
        if (userAgent.indexOf("Android") !== -1) os = "Android";
        if (userAgent.indexOf("like Mac") !== -1) os = "iOS";

        // Determine Browser
        let browser = 'Unknown Browser';
        if (userAgent.indexOf("Firefox") !== -1) browser = "Firefox";
        else if (userAgent.indexOf("SamsungBrowser") !== -1) browser = "Samsung Internet";
        else if (userAgent.indexOf("Opera") !== -1 || userAgent.indexOf("OPR") !== -1) browser = "Opera";
        else if (userAgent.indexOf("Trident") !== -1) browser = "Internet Explorer";
        else if (userAgent.indexOf("Edge") !== -1) browser = "Edge";
        else if (userAgent.indexOf("Chrome") !== -1) browser = "Chrome";
        else if (userAgent.indexOf("Safari") !== -1) browser = "Safari";

        const deviceType = isMobile ? 'mobile' : /iPad|Tablet/i.test(userAgent) ? 'tablet' : 'desktop';
        const deviceName = `${browser} on ${os}`;

        // Prepare current device info
        const now = new Date();
        const nowISO = now.toISOString();

        // Check if we need to update
        // We throttle updates to once every 5 minutes to allow for "isActive" refresh without spamming DB
        if (Date.now() - lastSyncRef.current < 5 * 60 * 1000) {
            return;
        }

        const devices = user.devices || [];
        const existingDeviceIndex = devices.findIndex(d => d.id === deviceId);

        let updatedDevices = [...devices];
        let hasChanges = false;

        if (existingDeviceIndex >= 0) {
            // Update existing
            const existing = devices[existingDeviceIndex];

            // Calculate usage duration since last active (approximate)
            let newTotalUsage = existing.totalUsageMinutes || 0;
            if (existing.lastActive) {
                const lastActiveDate = new Date(existing.lastActive);
                // If last active was within last 30 mins, add the diff
                const diffMinutes = (now.getTime() - lastActiveDate.getTime()) / (1000 * 60);
                if (diffMinutes < 30) {
                    // Just add a small increment for this session start, or rely on periodic updates
                    // For simplicity, we won't add efficient duration tracking here to avoid complex state
                }
            }

            const updatedDevice = {
                ...existing,
                lastActive: nowISO,
                name: deviceName,
                os: os,
                browser: browser,
                type: deviceType as 'desktop' | 'mobile' | 'tablet',
            };

            // Check if anything meaningful changed (besides lastActive which always changes)
            if (JSON.stringify(existing) !== JSON.stringify(updatedDevice)) {
                updatedDevices[existingDeviceIndex] = updatedDevice;
                hasChanges = true;
            }
        } else {
            // Add new
            const newDevice: UserDevice = {
                id: deviceId!,
                name: deviceName,
                type: deviceType as 'desktop' | 'mobile' | 'tablet',
                os: os,
                browser: browser,
                lastActive: nowISO,
                firstActive: nowISO,
                totalUsageMinutes: 0
            };
            updatedDevices.push(newDevice);
            hasChanges = true;
        }

        if (hasChanges) {
            console.log('[DeviceTracking] Updating device list', updatedDevices);
            const newUser = { ...user, devices: updatedDevices };

            // Update state
            setUser(newUser);

            // Persist to local storage
            localStorage.setItem('ledger_current_user', JSON.stringify(newUser));

            // Sync to Firebase
            if (updateUser) {
                updateUser(user.id, { devices: updatedDevices })
                    .catch(err => console.error('[DeviceTracking] Failed to sync devices:', err));
            }

            lastSyncRef.current = Date.now();
        }

        // Set up periodic "I am alive" ping every 5 minutes
        const pingInterval = setInterval(() => {
            const currentDevices = user.devices || [];
            const idx = currentDevices.findIndex(d => d.id === deviceId);
            if (idx >= 0) {
                // Update last active time
                const updated = [...currentDevices];
                updated[idx] = {
                    ...updated[idx],
                    lastActive: new Date().toISOString(),
                    totalUsageMinutes: (updated[idx].totalUsageMinutes || 0) + 5
                };

                // Silent update to firebase (don't trigger full app re-render if possible, or do)
                // Here we just follow the pattern
                if (updateUser) {
                    updateUser(user.id, { devices: updated });
                }
            }
        }, 5 * 60 * 1000);

        return () => clearInterval(pingInterval);

    }, [user?.id, updateUser, setUser]);
};
